import { useState, useEffect } from 'react'
import { Form as FinalForm, Field } from 'react-final-form'
import { useTranslation } from 'react-i18next'
import { styled, Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import Grid from '@mui/material/Grid'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Dialog from '../../control/Dialog'
import Textbox from '../../control/Textbox'
import Textarea from '../../control/Textarea'
import UploadInput from '../input/UploadInput'
import useContact, { Form, validate } from '../../hooks/backoffice/useContact'
import { useSelector } from 'react-redux'
import { RootStateType } from '../../redux/reducer/rootReducer'

type ManageContactProps = {}

const ManageContact = (props: ManageContactProps) => {
    const {
        alertMessageError,
        openDialogError,
        alertMessageOk,
        openDialogOk,
        openDialogBack,
        openDialogSave,
        onClickConfirmSave,
        onClickCancelSave,
        onClickCancelBack,
        onClickConfirmBack,
        onClickOk,
        onSubmitForm,
        callServiceGetInitial,
    } = useContact()
    const { t } = useTranslation()
    const classes = useStyles()

    const validates = (values: Form) => {
        const validation = validate(values, t) || {}
        return validation
    }

    const [initialValues, setInitialValues] = useState<Form>({})

    const { isLoggedIn } = useSelector(({ profile }: RootStateType) => profile)

    useEffect(() => {
        if (isLoggedIn) {
            callServiceGetInitialForm()
        }
    }, [isLoggedIn])

    const callServiceGetInitialForm = async () => {
        const { result } = await callServiceGetInitial()

        if (result?.data) {
            setInitialValues({
                coverImage: result?.data.coverImage ? [result?.data.coverImage] : [],
                organizationName: result?.data.organizationName,
                address: result?.data.address,
                telephone: result?.data.contactNumber,
                callCenter: result?.data.callCenter,
                email: result?.data.contactEmail,
                website: result?.data.website,
                line: result?.data.lineId,
            })
        }
    }

    return (
        <>
            <StyledDivHeader>
                <StyledTypoHeader variant="h6">{t('MENU.CONTACT_MANAGEMENT')}</StyledTypoHeader>
            </StyledDivHeader>
            <StyledDivForm>
                <FinalForm onSubmit={onSubmitForm} validate={validates} initialValues={initialValues}>
                    {({ handleSubmit, values, form }) => (
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2} className={classes.container}>
                                <Grid item xs={12}>
                                    <Typography>{t('LABEL.BACKOFFICE.CONTACT.IMAGE')}</Typography>
                                    <Field
                                        name="coverImage"
                                        label={t('LABEL.BACKOFFICE.UPLOAD')}
                                        placeholder={t('LABEL.BACKOFFICE.PLACEHOLDER_UPLOAD')}
                                        component={UploadInput}
                                        maxFile={1}
                                        displayLabelFileList={false}
                                        allowExtension={['image/jpg', 'image/jpeg', 'image/png']}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Field
                                        name="organizationName"
                                        label={t('LABEL.BACKOFFICE.CONTACT.OFFICE_NAME')}
                                        placeholder={t('LABEL.BACKOFFICE.CONTACT.OFFICE_NAME')}
                                        component={Textbox}
                                        fullWidth
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Field
                                        name="address"
                                        label={t('LABEL.BACKOFFICE.CONTACT.ADDRESS')}
                                        placeholder={t('LABEL.BACKOFFICE.CONTACT.ADDRESS')}
                                        component={Textarea}
                                        fullWidth
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Field
                                        name="telephone"
                                        label={t('LABEL.BACKOFFICE.CONTACT.TELEPHONE')}
                                        placeholder={t('LABEL.BACKOFFICE.CONTACT.TELEPHONE')}
                                        component={Textbox}
                                        fullWidth
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Field
                                        name="callCenter"
                                        label={t('LABEL.BACKOFFICE.CONTACT.CALL_CENTER')}
                                        placeholder={t('LABEL.BACKOFFICE.CONTACT.CALL_CENTER')}
                                        component={Textbox}
                                        fullWidth
                                        required
                                        maxLength={10}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Field
                                        name="email"
                                        label={t('LABEL.BACKOFFICE.CONTACT.EMAIL')}
                                        placeholder={t('LABEL.BACKOFFICE.CONTACT.EMAIL')}
                                        component={Textbox}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Field
                                        name="website"
                                        label={t('LABEL.BACKOFFICE.CONTACT.WEBSITE')}
                                        placeholder={t('LABEL.BACKOFFICE.CONTACT.WEBSITE')}
                                        component={Textbox}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Field
                                        name="line"
                                        label={t('LABEL.BACKOFFICE.CONTACT.LINE')}
                                        placeholder={t('LABEL.BACKOFFICE.CONTACT.LINE')}
                                        component={Textbox}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12} style={{ display: 'flex', justifyContent: 'end' }}>
                                    <div className={classes.divButton}>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            onClick={() => {
                                                form.reset()
                                            }}
                                            classes={{ root: classes.button }}
                                        >
                                            {'คืนค่า'}
                                        </Button>
                                    </div>
                                    <div className={classes.divButton}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleSubmit}
                                            classes={{ root: classes.button }}
                                        >
                                            {t('BUTTON.SAVE')}
                                        </Button>
                                    </div>
                                </Grid>
                            </Grid>
                        </form>
                    )}
                </FinalForm>
            </StyledDivForm>
            <Dialog
                type="confirm"
                title={t('ALERT.TITLE.NOTIFY')}
                content={t('ALERT.CONTENT.DISCARD_CHANGE')}
                isOpen={openDialogBack ? true : false}
                onClickCancel={onClickConfirmBack}
                onClickConfirm={onClickCancelBack}
                labelButtonCancel={t('BUTTON.LEAVE')}
                labelButtonConfirm={t('BUTTON.STAY')}
                disableClickBackdrop
                hideCloseButton
            />
            <Dialog
                type="confirm"
                title={t('ALERT.TITLE.NOTIFY')}
                content={'ยืนยันบันทึกข้อมูล'}
                isOpen={openDialogSave}
                onClickConfirm={onClickConfirmSave}
                onClickCancel={onClickCancelSave}
                labelButtonCancel={t('BUTTON.CANCEL')}
                labelButtonConfirm={t('BUTTON.CONFIRM')}
                disableClickBackdrop
                hideCloseButton
            />
            <Dialog
                type="ok"
                title={t('ALERT.TITLE.NOTIFY')}
                content={alertMessageOk}
                isOpen={openDialogOk}
                onClickOk={onClickOk}
                labelButtonOk={t('BUTTON.OK')}
                disableClickBackdrop
                hideCloseButton
            />
            <Dialog
                type="ok"
                severity="error"
                title={t('ALERT.TITLE.ERROR')}
                content={alertMessageError}
                isOpen={openDialogError}
                onClickOk={onClickOk}
                labelButtonOk={t('BUTTON.OK')}
                disableClickBackdrop
                hideCloseButton
            />
        </>
    )
}

export default ManageContact

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '30px',
    },
    divButton: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginLeft: theme.spacing(1),
    },
    button: {
        width: '125px',
        minHeight: '37px',
        fontSize: '14px !important',
    },
    icon: {
        fontSize: '70px !important',
        color: '#318AF14D',
        opacity: 30,
    },
}))

const StyledDivHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    paddingBottom: '15px',
}))

const StyledTypoHeader = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
}))

const StyledDivForm = styled('div')(({ theme }) => ({
    width: '100%',
    height: '100%',
    padding: '15px 30px',
    borderRadius: '5px',
    backgroundColor: theme.palette.text.secondary,
    boxShadow: theme.palette.getContrastText('shadow-bottom-gray'),
}))
